import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Mobile2FA from "../../components/auth/Mobile2FA";
import { useCookieConsent } from "../../context/useCookieConsent";
import { areCookiesAccepted } from "../../js/utils/cookieConsent";

const SignIn = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { showCookieConsent } = useCookieConsent();

	useEffect(() => {
		if (!areCookiesAccepted()) {
			showCookieConsent();
		}
	}, [showCookieConsent]);

	// Get invitation parameters from URL
	const acceptingInvitation =
		searchParams.get("acceptinginvitation") === "true";
	const inviteParams = {
		partyId: searchParams.get("partyId"),
		contestId: searchParams.get("contestId"),
		memberId: searchParams.get("memberId"),
		role: searchParams.get("role"),
		inviteType: searchParams.get("inviteType"),
		gameMasterID: searchParams.get("gameMasterID"),
		contest: searchParams.get("contest"),
		inviter: searchParams.get("inviter"),
		party: searchParams.get("party"),
		partyName: searchParams.get("partyName"),
		invitedBy: searchParams.get("invitedby"),
		acceptingInvitation,
	};

	function onSubmit(values) {
		console.log("🔐 SignIn onSubmit called");

		// First priority: Check if there's a return URL stored from before sign-in
		const returnAfterSignIn = localStorage.getItem("returnAfterSignIn");
		console.log("📍 Checking for returnAfterSignIn:", returnAfterSignIn);

		if (returnAfterSignIn) {
			localStorage.removeItem("returnAfterSignIn");
			console.log("📍 Navigating to stored return URL:", returnAfterSignIn);
			navigate(returnAfterSignIn);
			return;
		}

		// Second priority: If this sign-in was initiated from an invitation
		if (acceptingInvitation) {
			console.log("📍 Handling invitation flow");
			// Determine if this is a general invite (no contestId and no partyId)
			const isGeneralInvite = !inviteParams.contestId && !inviteParams.partyId;
			if (isGeneralInvite) {
				// General member-recruited invite: go straight to newsfeed
				navigate("/newsfeed");
				return;
			}

			// Build accept-invite path segments
			const partyIdToUse = inviteParams.partyId || "invite";
			let path = `/accept-invite/${encodeURIComponent(partyIdToUse)}`;

			if (inviteParams.contestId) {
				path += `/${encodeURIComponent(inviteParams.contestId)}`;
			}
			if (inviteParams.memberId) {
				path += `/${encodeURIComponent(inviteParams.memberId)}`;
			}
			if (inviteParams.role) {
				path += `/${encodeURIComponent(inviteParams.role)}`;
			}
			if (inviteParams.inviteType) {
				// Route param name is `type` in router
				path += `/${encodeURIComponent(inviteParams.inviteType)}`;
			}

			// Preserve any additional query params (exclude ones moved to path and the flag itself)
			const excludeKeys = new Set([
				"acceptinginvitation",
				"partyId",
				"contestId",
				"memberId",
				"role",
				"inviteType",
			]);
			const extraQuery = new URLSearchParams();
			for (const [key, value] of searchParams.entries()) {
				if (!excludeKeys.has(key) && value !== null && value !== "") {
					extraQuery.append(key, value);
				}
			}
			const queryString = extraQuery.toString();
			const finalURL = `${path}${queryString ? `?${queryString}` : ""}`;
			navigate(finalURL);
			return;
		}

		// Default for all other cases (navigate to home/newsfeed)
		console.log("📍 No return URL or invitation, navigating to /");
		navigate("/");
	}

	return (
		<div className="min-h-screen w-full bg-slate-50 transition-colors dark:bg-slate-950">
			<div className="flex h-full min-h-screen flex-col items-center justify-center px-4 py-16">
				<div className="w-full max-w-md rounded-2xl border border-slate-200/70 bg-white/90 shadow-xl backdrop-blur-sm transition-colors duration-300 dark:border-slate-800/70 dark:bg-slate-900/90">
					<div className="px-6 py-8 sm:px-8 sm:py-10">
						<div className="mb-6 text-center">
							<p className="text-xs uppercase tracking-[0.3em] text-slate-400 dark:text-slate-500">
								Glow Game
							</p>
							<h2 className="mt-2 text-3xl font-semibold text-slate-900 transition-colors dark:text-slate-100">
								Join the Glow Game!
							</h2>
							<p className="mt-3 text-sm text-slate-600 transition-colors dark:text-slate-400">
								Enter your phone number to sign in. We will create an account
								for you automatically if you need one.
							</p>
						</div>
						<div className="rounded-xl bg-slate-100/60 p-4 transition-colors dark:bg-slate-800/60">
							<Mobile2FA onSubmit={onSubmit} />
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SignIn;
